// @ts-nocheck
import React, {useState} from 'react';
import {t} from 'ttag';
import {Box, Button, TextField} from '@mui/material';
import validationSchema from './validationSchema';
import {Add} from '@mui/icons-material';
import {useGetRefundSubjectMutation} from '../../../../../../../store/apis/ccs';
import RTKQueryFeedback from '../../../../../../../components/common/RTKQueryFeedback';
import ConfirmDialog from '../../../../../../../components/common/ConfirmDialog';
import {useSnackbar} from 'notistack';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {RefundSubject} from "../../../../../../../types/RefundSubject.type";
import {MANUAL_TAX_TYPES} from '../crud-forms/create-edit/taxTypes';

interface RefundSubjectImportFormProps {
  refundSubjects: [];
  refundType: string;
  officeCode: string;
  onImport: (rs: RefundSubject) => RefundSubject;
}

const RefundSubjectImportForm = ({refundSubjects = [], refundType = null, officeCode = null, onImport = () => null} : RefundSubjectImportFormProps) => {

  const {enqueueSnackbar} = useSnackbar();
  const [getRefundSubject, {isLoading}] = useGetRefundSubjectMutation();
  const [pendingManualSubject, setPendingManualSubject] = useState(null);
  const {control, handleSubmit} = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      refundType,
      officeCode,
      companyCode: '',
      documentNumber: ''
    }
  });

  const buildManualSubject = (values, data = null) => {
    const currency = data?.amounts?.find(a => a.currency)?.currency ?? null;
    const FALLBACK_AMOUNT_TYPES = ['totalFareRefund', 'totalFarePaid', 'totalPaidTaxes', 'totalAmount', 'totalFareUsed', 'totalCancellationFee', 'totalRefund', 'totalFareUsedWithCancellationFee', 'totalUsedTaxesWithNonRefTaxes'];
    const taxes = data?.taxes?.length
      ? data.taxes.map((t, localId) => ({...t, localId, modified: false}))
      : [];
    const taxesTotal = taxes.filter(t => t.active !== false).reduce((sum, t) => sum + (t.currentValue ?? 0), 0);
    const refundableTaxesTotal = taxes.filter(t => t.type === 'refundableTaxes' && t.active !== false).reduce((sum, t) => sum + (t.currentValue ?? 0), 0);
    const rawAmounts = data?.amounts?.length
      ? data.amounts.map((a, localId) => ({...a, localId, modified: false}))
      : FALLBACK_AMOUNT_TYPES.map((type, localId) => ({localId, type, value: 0, currentValue: 0, currency, modified: false}));
    const fareAmount = rawAmounts.find(a => a.type === 'totalFarePaid');
    const fareValue = fareAmount?.currentValue ?? 0;
    const fareUsed = rawAmounts.find(a => a.type === 'totalFareUsed')?.currentValue ?? 0;
    const cancellationFee = rawAmounts.find(a => a.type === 'totalCancellationFee')?.currentValue ?? 0;
    const fareUsedWithCancellation = parseFloat((fareUsed + cancellationFee).toFixed(2));
    const usedTaxesWithNonRef = parseFloat((taxesTotal - refundableTaxesTotal).toFixed(2));
    const amounts = rawAmounts.map(a => {
      if (a.type === 'totalPaidTaxes') return {...a, currentValue: taxesTotal, value: taxesTotal};
      if (a.type === 'totalAmount') return {...a, currentValue: fareValue + taxesTotal, value: fareValue + taxesTotal};
      if (a.type === 'totalFareUsedWithCancellationFee') return {...a, currentValue: fareUsedWithCancellation, value: fareUsedWithCancellation};
      if (a.type === 'totalUsedTaxesWithNonRefTaxes') return {...a, currentValue: usedTaxesWithNonRef, value: usedTaxesWithNonRef};
      return a;
    });
    const initialCoupons = [{localId: 0, originAirport: '', destinationAirport: '', carrier: '', flightNumber: '', fareBasis: '', couponStatus: 'O', datetime: ''}];
    const coupons = data?.coupons?.length ? data.coupons : initialCoupons;
    const passengers = data?.passengers ?? [];
    const paymentOptions = data?.paymentOptions ?? [];
    return {
      companyCode: values.companyCode,
      documentNumber: values.documentNumber,
      reservationCode: data?.reservationCode ?? '',
      gdsCode: data?.gdsCode ?? null,
      issueDate: data?.issueDate ?? null,
      atcMask: data?.atcMask ?? null,
      ...(currency != null ? {currencyCode: currency} : {}),
      passengers,
      coupons,
      amounts,
      taxes,
      paymentOptions,
      modified: false,
      manual: true,
      original: {amounts, coupons, taxes, paymentOptions, passengers},
    };
  };

  const importHandler = (values) => {
    if (refundSubjects?.some(r => r.documentNumber === values.documentNumber)) {
      return enqueueSnackbar(t`Refund subject already in list.`, {variant: 'warning'});
    }
    const isMco = values.documentNumber?.startsWith('4020');
    getRefundSubject(values).then(({data, error}) => {
      if (!isMco && data?.atcMask != null) return onImport(data?.manual ? buildManualSubject(values, data) : data);
      if (isMco || data || error) {
        setPendingManualSubject(buildManualSubject(values, data));
      }
    });
  };

  return (
    <>
      <ConfirmDialog
        open={Boolean(pendingManualSubject)}
        deletePrefix={false}
        title={t`Document not found`}
        confirmText={t`Fill in Manually`}
        cancelText={t`Go Back`}
        variant='info'
        onConfirm={() => { onImport(pendingManualSubject); setPendingManualSubject(null); }}
        onCancel={() => setPendingManualSubject(null)}
      >
        {t`The document data could not be imported. Do you want to fill in the details manually?`}
      </ConfirmDialog>
      <Box display={'flex'} justifyContent={'space-between'} py={2}>
        <RTKQueryFeedback isLoading={isLoading}/>
        <Box maxWidth={'100px'}>
          <Controller
            control={control}
            name={'companyCode'}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                label={t`Code`}
                name={'companyCode'}
                color={'primary'}
                variant={'outlined'}
                error={Boolean(error)}
                helperText={error?.message}
                placeholder={'ex. 331'}
                size={'small'}
                required
                fullWidth
              />
            )}/>
        </Box>
        <Box flexGrow={1} px={1}>
          <Controller
            control={control}
            name={'documentNumber'}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                label={t`Document number`}
                name={'documentNumber'}
                color={'primary'}
                variant={'outlined'}
                error={Boolean(error)}
                helperText={error?.message}
                placeholder={'ex. 24001732'}
                size={'small'}
                required
                fullWidth
              />
            )}/>
        </Box>
        <Box>
          <Button variant={'contained'} onClick={handleSubmit(importHandler)} size={'medium'} endIcon={<Add/>}>
            {t`Add`}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default RefundSubjectImportForm;